import { attach, createEffect, createEvent, createStore, sample } from 'effector';
import { isNil } from 'ramda';
import { httpClient, httpUnauthorized, setAuthHeaderProvider } from '@/api';
import { readJson, remove, writeJson } from '@/lib/storage';
import { resumeFx } from '../search';
import type { AuthSession, AuthTokens, LoginPayload } from './types';

const STORAGE_KEY = process.env.APP_AUTH_STORAGE_KEY ?? 'app:auth';

const isExpired = (tokens: AuthTokens): boolean => !isNil(tokens.expiresAt) && tokens.expiresAt <= Date.now();

const initialSession = (() => {
  const stored = readJson<AuthSession>(STORAGE_KEY);
  if (!stored || isExpired(stored.tokens)) return null;
  return stored;
})();

export const sessionRestored = createEvent<AuthSession>();
export const loggedOut = createEvent();

export const $session = createStore<AuthSession | null>(initialSession)
  .on(sessionRestored, (_, session) => session)
  .reset(loggedOut);

export const $isAuthenticated = $session.map((s) => s !== null);
export const $user = $session.map((s) => s?.user ?? null);
export const $tokens = $session.map((s) => s?.tokens ?? null);

export const loginFx = createEffect<LoginPayload, AuthSession>((payload) =>
  httpClient.post<AuthSession>('/auth/login', payload, { auth: false }),
);

export const logoutFx = attach({
  source: $tokens,
  effect: createEffect<AuthTokens | null, void>(async (tokens) => {
    if (!tokens) return;
    await httpClient.post('/auth/logout', undefined).catch(() => undefined);
  }),
});

export const refreshFx = attach({
  source: $tokens,
  effect: createEffect<AuthTokens | null, AuthSession>(async (tokens) => {
    if (!tokens?.refreshToken) throw new Error('No refresh token');
    return httpClient.post<AuthSession>(
      '/auth/refresh',
      { refreshToken: tokens.refreshToken },
      { auth: false },
    );
  }),
});

sample({ clock: loginFx.doneData, target: sessionRestored });
sample({ clock: refreshFx.doneData, target: sessionRestored });
// после успешного входа — определить, куда роутить (active search → /processing,
// готовый отчёт → /report, иначе → /onboard).
sample({ clock: loginFx.doneData, target: resumeFx });
sample({ clock: [logoutFx.done, logoutFx.fail], target: loggedOut });
sample({ clock: httpUnauthorized, target: loggedOut });

const persistSessionFx = createEffect((session: AuthSession | null) => {
  if (session) writeJson(STORAGE_KEY, session);
  else remove(STORAGE_KEY);
});

sample({ clock: $session, target: persistSessionFx });

setAuthHeaderProvider(() => {
  const tokens = $tokens.getState();
  const headers: Record<string, string> = {};
  if (tokens?.accessToken) headers.Authorization = `Bearer ${tokens.accessToken}`;
  return headers;
});
